import { ChevronDown } from "lucide-react";
import { Fragment, type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
  DataTable,
  NARROW_ONLY,
  SortableHeadCell,
  SortButton,
  StateText,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableFacts,
  TableHead,
  TableHeadCell,
  TableItem,
} from "@/components/data-table";
import { Pager } from "@/components/pager";
import { ToolIcon } from "@/components/tool-icon";
import { ToolName } from "@/components/tool-name";
import { Button } from "@/components/ui/button";
import {
  type AdminMcpCallsParams,
  type McpCallSortKey,
  type McpToolCall,
  useAdminMcpCalls,
} from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useLanguage } from "@/lib/i18n/language";
import { issuePath } from "@/lib/issues";
import { onControl } from "@/lib/row-click";
import { factLine } from "@/lib/table-facts";
import type { SortColumns, TableSortState } from "@/lib/table-sort";
import { useToolTitles } from "@/lib/tool-titles";
import { cn } from "@/lib/utils";

import { formatDuration, offsetBackInRange, pageRange } from "../utils";
import { CallDetail } from "./call-detail";
import {
  callSortOrder,
  callStatus,
  callStatusLabel,
  NOTHING_SEEN,
  type SeenCallValues,
} from "./sort-order";

/** The call log's columns, each with the order a first click sorts in. */
export const CALL_SORT_COLUMNS: SortColumns<McpCallSortKey> = {
  created_at: "desc",
  user: "asc",
  tool: "asc",
  duration: "desc",
  status: "asc",
};

/** The call log's first sort: the newest call first. */
export const CALL_SORT_INITIAL = { key: "created_at", dir: "desc" } as const;

const PAGE_SIZE = 25;

/** Time, person, tool, duration, status, details. */
const COLUMNS = 6;

/** What the call log shows: its filters, without the sort and the page. */
export type CallLogQuery = Omit<
  AdminMcpCallsParams,
  "sortBy" | "sortDir" | "sortOrder" | "limit" | "offset"
>;

interface CallLogProps {
  /** The calls to show: a time range, tools, people, an outcome, an issue. */
  query: CallLogQuery;
  /** The sort, from `useTableSort` or `useUrlTableSort` with `CALL_SORT_COLUMNS`; the server sorts. */
  sort: TableSortState<McpCallSortKey>;
  /** The table's name for screen readers. */
  label: string;
  /** Clears the filters, offered when they leave the log empty. */
  onReset?: () => void;
  /**
   * Tools and error kinds the log may show besides those the dashboard knows,
   * such as the past quarter's, so a sort by tool or status puts them by
   * their words among the others rather than after them.
   */
  seen?: SeenCallValues;
}

/** Who made a call: their name, else their email, else their Microsoft id; null without sign-in. */
function callerName(call: McpToolCall): string | null {
  return call.user_name?.trim() || call.user_email || call.user_oid || null;
}

/**
 * The connector's tool calls in the Tools page's style: time, person (name
 * and email), tool (icon, title and name), duration and outcome, which says
 * "OK" quietly and a failure's kind in red, linking to its issue. Sorted,
 * filtered and paged on the server; the rows dim while the next ones load.
 * Every column sorts. A row opens its details (the Odoo figures of the call)
 * under it, by its chevron or a click anywhere on the row. On a phone each row
 * leads with the tool, with the time, the duration and the person under it.
 * Metadata only: the connector never stores what was asked or answered.
 * A sort by tool or status follows the words the table shows in the
 * dashboard's language, not the codes the server holds.
 */
export function CallLog({ query, sort, label, onReset, seen = NOTHING_SEEN }: CallLogProps) {
  const { t } = useTranslation();
  const language = useLanguage();
  const titles = useToolTitles();
  // The page belongs to one set of filters and one sort: any change starts again at the top.
  const signature = JSON.stringify([query, sort.sort]);
  const [page, setPage] = useState({ signature, offset: 0 });
  const offset = page.signature === signature ? page.offset : 0;
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());

  const callsQuery = useAdminMcpCalls({
    ...query,
    sortBy: sort.sort?.key,
    sortDir: sort.sort?.dir,
    // Part of the query's key, so another language or catalogue asks again in the new order.
    sortOrder: callSortOrder(sort.sort?.key, titles, seen, language),
    limit: PAGE_SIZE,
    offset,
  });
  const data = callsQuery.data ?? { items: [], total: 0, limit: PAGE_SIZE, offset: 0 };
  const loading = callsQuery.isFetching;

  // This page came back empty past the end (at midnight older calls drop out of
  // the period): go to the last page that still has calls.
  const backInRange =
    callsQuery.data && !callsQuery.isPlaceholderData && callsQuery.data.offset === offset
      ? offsetBackInRange(callsQuery.data, PAGE_SIZE)
      : null;
  if (backInRange !== null) {
    setPage({ signature, offset: backInRange });
  }

  const toggle = (id: number) =>
    setOpen((ids) => {
      const next = new Set(ids);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });
  // A click on the row opens its details, but not one on a link or a button in it.
  const onRowClick = (event: MouseEvent<HTMLTableRowElement>, id: number) => {
    if (!onControl(event)) {
      toggle(id);
    }
  };

  const { from, to } = pageRange(data.offset, data.items.length, data.total);

  return (
    <div className="space-y-4">
      <DataTable label={label} busy={loading}>
        <TableHead>
          <SortableHeadCell {...sort.head("created_at")} narrow="hide">
            {t("analytics.time")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("user")} narrow="hide">
            {t("analytics.person")}
          </SortableHeadCell>
          <SortableHeadCell
            {...sort.head("tool")}
            after={
              // The time's sort stays in the phone layout, where its column left.
              <span className={cn("ml-3", NARROW_ONLY)}>
                <SortButton {...sort.head("created_at")} sayOrder>
                  {t("analytics.time")}
                </SortButton>
              </span>
            }
          >
            {t("analytics.tool")}
          </SortableHeadCell>
          {/* Leaves when the table's own room is tight; the details say it too. */}
          <SortableHeadCell
            {...sort.head("duration")}
            align="right"
            narrow="hide"
            className="@max-3xl:hidden"
          >
            {t("analytics.duration")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("status")} align="right">
            {t("analytics.status")}
          </SortableHeadCell>
          <TableHeadCell className="w-px">
            <span className="sr-only">{t("analytics.detail.column")}</span>
          </TableHeadCell>
        </TableHead>
        <tbody>
          {callsQuery.isError ? (
            <tr className="border-t">
              <td colSpan={COLUMNS} className="text-muted px-3 py-8 text-center">
                {t("analytics.callsLoadError")}{" "}
                <button
                  type="button"
                  onClick={() => void callsQuery.refetch()}
                  className="text-accent font-medium hover:underline"
                >
                  {t("common.tryAgain")}
                </button>
              </td>
            </tr>
          ) : data.items.length === 0 ? (
            <TableEmptyRow colSpan={COLUMNS} onReset={!loading ? onReset : undefined}>
              {loading ? t("common.loadingEllipsis") : t("analytics.callsEmpty")}
            </TableEmptyRow>
          ) : (
            data.items.map((call) => {
              const who = callerName(call);
              const expanded = open.has(call.id);
              const detailId = `call-${call.id}`;
              return (
                <Fragment key={call.id}>
                  <tr
                    className={cn(
                      "hover:bg-accent-light/40 cursor-pointer border-t",
                      expanded && "bg-accent-light/40",
                    )}
                    onClick={(event) => onRowClick(event, call.id)}
                  >
                    <TableCell narrow="hide" className="text-muted whitespace-nowrap tabular-nums">
                      {formatDateTime(call.created_at)}
                    </TableCell>
                    <TableCell narrow="hide">
                      <Caller call={call} />
                    </TableCell>
                    <TableCell>
                      <TableItem
                        icon={<ToolIcon name={call.tool} className={TABLE_ICON_CLASS} />}
                        title={<ToolName name={call.tool} titles={titles} />}
                        facts={
                          // When and how long on one line, who on the next.
                          <TableFacts>
                            {factLine([
                              formatDateTime(call.created_at),
                              call.duration_ms != null && formatDuration(call.duration_ms),
                            ])}
                            <br />
                            <span className={who ? undefined : "text-muted"}>
                              {who ?? t("analytics.withoutSignIn")}
                            </span>
                          </TableFacts>
                        }
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      narrow="hide"
                      className="text-muted whitespace-nowrap tabular-nums @max-3xl:hidden"
                    >
                      {formatDuration(call.duration_ms)}
                    </TableCell>
                    <TableCell align="right">
                      <Outcome call={call} />
                    </TableCell>
                    <TableCell align="right" className="py-1 pr-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 pointer-coarse:size-10"
                        aria-expanded={expanded}
                        aria-controls={expanded ? detailId : undefined}
                        aria-label={t("analytics.detail.toggle", {
                          time: formatDateTime(call.created_at),
                        })}
                        onClick={() => toggle(call.id)}
                      >
                        <ChevronDown
                          aria-hidden="true"
                          className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")}
                        />
                      </Button>
                    </TableCell>
                  </tr>
                  {expanded && (
                    <tr id={detailId} className="bg-accent-light/40">
                      <td colSpan={COLUMNS} className="px-3 pt-1 pb-3">
                        <CallDetail call={call} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </DataTable>

      <Pager
        from={from}
        to={to}
        total={data.total}
        previousLabel={t("analytics.previousPage")}
        nextLabel={t("analytics.nextPage")}
        canPrevious={offset > 0 && !loading}
        canNext={offset + PAGE_SIZE < data.total && !loading}
        onPrevious={() => setPage({ signature, offset: Math.max(0, offset - PAGE_SIZE) })}
        onNext={() => setPage({ signature, offset: offset + PAGE_SIZE })}
      />
    </div>
  );
}

/** Who made a call: their name over their email, or "Zonder aanmelding". */
function Caller({ call }: { call: McpToolCall }) {
  const { t } = useTranslation();
  const name = call.user_name?.trim();
  if (!name && !call.user_email && !call.user_oid) {
    return <span className="text-muted">{t("analytics.withoutSignIn")}</span>;
  }
  return (
    <span className="block min-w-0">
      <span className={cn("text-foreground block", !name && "break-all")}>
        {name || call.user_email || call.user_oid}
      </span>
      {name && call.user_email && (
        // The email leaves when the table's own room is tight; the name stays.
        <span className="text-muted block text-xs break-all @max-3xl:hidden">
          {call.user_email}
        </span>
      )}
    </span>
  );
}

/** "OK" quietly, or a failure's kind in red, linking to its issue. */
function Outcome({ call }: { call: McpToolCall }) {
  const label = callStatusLabel(callStatus(call));
  if (call.ok) {
    return <StateText>{label}</StateText>;
  }
  const className = "text-destructive text-xs font-medium";
  return call.issue_key ? (
    <Link to={issuePath(call.issue_key)} className={cn(className, "hover:underline")}>
      {label}
    </Link>
  ) : (
    <span className={className}>{label}</span>
  );
}
